import { RequestUtils } from '@/utils/request/requestUtil'
import { BaseWebUrl } from '@/utils/request/baseWebUrl'
import { BrowserFileUtils } from '@/platform/utils/BrowserFileUtils'
import { API_ENDPOINTS } from '@/config/apiEndpoints'
import {
  requestJson,
  type JsonRecord,
  type RequestTraceCallback,
} from '@/utils/request/apiRequest'

const webUrl = new BaseWebUrl()
const cgEndpoints = API_ENDPOINTS.cg
const cgApiEndpoints = API_ENDPOINTS.cgApi
const websiteEndpoints = API_ENDPOINTS.website

/**
 * 从发布版本接口的多种返回字段中提取版本号。
 * @param data 发布版本接口原始返回。
 * @returns 归一化后的版本号；没有可用字段时返回 `0`。
 */
function normalizeVersion(data: JsonRecord): string {
  const candidates = [
    data.version,
    data.ver,
    (data.data as JsonRecord | undefined)?.version,
    (data.data as JsonRecord | undefined)?.ver,
    (data.data as JsonRecord | undefined)?.proj_ver,
  ]
  for (const value of candidates) {
    const text = String(value ?? '').trim()
    if (text) return text
  }
  return '0'
}

/**
 * 从 pkgquery 返回中提取作品 gindex。
 * @param data pkgquery 原始返回。
 * @returns 可用的 gindex；接口未返回时为 null。
 */
function normalizeGindex(data: JsonRecord): string | null {
  const keys = ['gindex', 'webIndex', 'web_index', 'webindex', 'game_index', 'gameId', 'game_id']
  const getIdFromUrl = (value: unknown): string => {
    const text = String(value ?? '').trim()
    return text.match(/\/game\/(\d+)/)?.[1] ?? ''
  }
  const visited = new Set<unknown>()
  const pick = (value: unknown): string | null => {
    if (!value || visited.has(value)) return null
    if (typeof value !== 'object') return null
    visited.add(value)
    if (Array.isArray(value)) {
      for (const item of value) {
        const matched = pick(item)
        if (matched) return matched
      }
      return null
    }
    const record = value as JsonRecord
    const progress = Number(record.progress ?? record.data_progress ?? 0)
    const info = record.info as JsonRecord | undefined
    if (progress === 7) {
      const urlId = getIdFromUrl(info?.url ?? record.url)
      if (urlId) return urlId
    }
    for (const key of keys) {
      const text = String(record[key] ?? '').trim()
      if (text) return text
    }
    const urlId = getIdFromUrl(record.url)
    if (urlId) return urlId
    for (const item of Object.values(record)) {
      const matched = pick(item)
      if (matched) return matched
    }
    return null
  }
  return pick(data)
}

/**
 * 校验 CG 接口返回是否表示成功。
 * @param data 接口原始返回。
 * @param fallbackMessage 接口未给出错误文案时使用的默认错误信息。
 * @returns 原始返回数据。
 * @throws 当 ret/status 明确表示失败时抛出接口错误信息。
 */
function assertCgSuccess(data: JsonRecord, fallbackMessage: string): JsonRecord {
  const payload = (data.data as JsonRecord | undefined) ?? data
  const successCandidates = [payload.ret, data.ret, payload.status, data.status]
  const hasExplicitStatus = successCandidates.some((value) => value !== undefined && value !== null && value !== '')
  if (!hasExplicitStatus) return data
  const isSuccess = successCandidates.some((value) => String(value ?? '').trim() === '1')
  if (isSuccess) return data
  const message = String(payload.msg ?? data.msg ?? fallbackMessage).trim() || fallbackMessage
  throw new Error(message)
}

/**
 * 获取当前工程下一次发布版本号。
 * @param sessionId 当前登录 session_id。
 * @param guid 工程 GUID。
 * @param onTrace 请求日志回调。
 * @returns 服务端分配的发布版本号。
 */
export async function getPublishVersion(sessionId: string, guid: string, onTrace?: RequestTraceCallback): Promise<string> {
  const data = await requestJson(webUrl.baseCg, cgEndpoints.getGameVersion, {
    params: { sessionid: sessionId, guid, channel_id: 0 },
    traceLabel: `GET ${cgEndpoints.getGameVersion}`,
    onTrace,
    errorPrefix: '发布接口请求失败',
  })
  return normalizeVersion(data)
}

/**
 * 查询指定发布版本对应的作品 gindex。
 * @param sessionId 当前登录 session_id。
 * @param guid 工程 GUID。
 * @param version 发布版本号。
 * @param onTrace 请求日志回调。
 * @returns 当前版本对应的 gindex；不存在时返回 null。
 */
export async function getPublishGindex(
  sessionId: string,
  guid: string,
  version: string,
  onTrace?: RequestTraceCallback,
): Promise<string | null> {
  const data = await requestJson(webUrl.baseCg, cgEndpoints.getPkgQuery, {
    params: { sessionid: sessionId, pguid: guid, target: 2, version },
    signed: true,
    traceLabel: `GET ${cgEndpoints.getPkgQuery}`,
    onTrace,
    errorPrefix: '发布接口请求失败',
  })
  return normalizeGindex(data)
}

/**
 * 检查当前账号是否有工程发布权限。
 * @param sessionId 当前登录 session_id。
 * @param guid 工程 GUID。
 * @param onTrace 请求日志回调。
 * @returns 发布权限接口原始返回。
 */
export async function checkProjectPublishAuth(sessionId: string, guid: string, onTrace?: RequestTraceCallback): Promise<JsonRecord> {
  return requestJson(webUrl.baseCg, cgEndpoints.checkProjectAuth, {
    params: { sessionid: sessionId, guid, channel_id: 0, target: 2 },
    traceLabel: `GET ${cgEndpoints.checkProjectAuth}`,
    onTrace,
    errorPrefix: '发布接口请求失败',
  })
}

/**
 * 检查首次发布前是否还需要补充网页标签。
 * @param params 当前用户、session 与工程 GUID。
 * @param onTrace 请求日志回调。
 * @returns 标签检查接口原始返回。
 */
export async function checkIfNeedTag(params: {
  uid: number
  sessionId: string
  guid: string
}, onTrace?: RequestTraceCallback): Promise<JsonRecord> {
  return requestJson(webUrl.baseWebsite, websiteEndpoints.checkIfAddTag, {
    params: {
      uid: params.uid,
      sessionid: params.sessionId,
      guid: params.guid,
    },
    signed: true,
    traceLabel: `GET ${websiteEndpoints.checkIfAddTag}`,
    onTrace,
    errorPrefix: '发布接口请求失败',
  })
}

/**
 * 判断标签检查接口返回是否表示缺少标签。
 * @param data 标签检查接口原始返回。
 * @returns 需要补标签返回 true，否则返回 false。
 */
export function isTagMissing(data: JsonRecord): boolean {
  const payload = (data.data as JsonRecord | undefined) ?? data
  const candidates = [
    payload.need_add_tag,
    payload.needTag,
    payload.need_tag,
    payload.is_need_add_tag,
  ]
  for (const value of candidates) {
    if (value === 1 || value === true || value === '1' || value === 'true') {
      return true
    }
  }
  const statusCandidates = [payload.status, data.status, payload.ret, data.ret]
  for (const value of statusCandidates) {
    if (value === undefined || value === null || value === '') continue
    const text = String(value).trim()
    if (!text) continue
    if (text === '1') return false
    return true
  }
  const msg = String(data.msg ?? payload.msg ?? '').toLowerCase()
  return msg.includes('标签') && (msg.includes('先设置') || msg.includes('未设置'))
}

/**
 * 检查发布表单文本敏感词。
 * @param params session、检查类型与待检查文本。
 * @param onTrace 请求日志回调。
 * @returns 敏感词接口原始返回。
 */
export async function checkBlackWords(params: {
  sessionId: string
  type: number
  content: string
}, onTrace?: RequestTraceCallback): Promise<JsonRecord> {
  return requestJson(webUrl.baseCg, cgEndpoints.checkBlackWords, {
    params: {
      sessionid: params.sessionId,
      type: params.type,
      content: params.content,
    },
    traceLabel: `GET ${cgEndpoints.checkBlackWords}[type=${params.type}]`,
    onTrace,
    errorPrefix: '发布接口请求失败',
  })
}

/**
 * 调用最终发布接口生成作品访问路径。
 * @param params 最终发布接口查询参数。
 * @param onTrace 请求日志回调。
 * @returns 最终发布接口原始返回，成功时包含作品访问地址或 gindex。
 */
export async function publishProjectRequest(
  params: Record<string, string | number>,
  onTrace?: RequestTraceCallback,
): Promise<JsonRecord> {
  onTrace?.(
    'running',
    `get_game_path params · ${JSON.stringify(params)}`,
  )
  const data = await requestJson(webUrl.baseCg, cgEndpoints.getGamePath, {
    params,
    signed: true,
    traceLabel: `GET ${cgEndpoints.getGamePath}`,
    onTrace,
    errorPrefix: '发布接口请求失败',
  })
  onTrace?.('done', `get_game_path response · ${JSON.stringify(data)}`)
  return data
}

/**
 * 获取发布作品封面图上传签名。
 * @param params guid、版本、session 与封面文件大小。
 * @param onTrace 请求日志回调。
 * @returns 又拍云上传所需 bucket、path、sign、date。
 */
export async function getUploadPicSign(
  params: {
    sessionId: string
    guid: string
    version: string
    length: number
  },
  onTrace?: RequestTraceCallback,
): Promise<JsonRecord> {
  const data = await requestJson(webUrl.baseCg, cgEndpoints.uploadPicSign, {
    params: {
      sessionid: params.sessionId,
      guid: params.guid,
      ver: params.version,
      length: params.length,
    },
    traceLabel: `GET ${cgEndpoints.uploadPicSign}`,
    onTrace,
    errorPrefix: '封面上传签名获取失败',
  })
  return assertCgSuccess(data, '封面上传签名获取失败')
}

/**
 * 上报 3.0 发布流程中的结果统计。
 * @param params get_result 查询参数，参与接口签名。
 * @param bodyParts 按 3.0 顺序写入并逐项转十六进制的统计字段。
 * @param onTrace 请求日志回调。
 */
export async function submitPublishResult(
  params: Record<string, string | number>,
  bodyParts: Array<string | number>,
  onTrace?: RequestTraceCallback,
): Promise<JsonRecord> {
  const body = bodyParts.map((item) => BrowserFileUtils.stringtoHex(String(item))).join('&')
  onTrace?.('running', `get_result params · ${JSON.stringify(params)}`)
  const data = await requestJson(webUrl.baseCg, cgEndpoints.getGameResult, {
    method: 'PUT',
    params,
    signed: true,
    body,
    headers: { 'Content-Type': 'application/octet-stream' },
    traceLabel: `PUT ${cgEndpoints.getGameResult}`,
    onTrace,
    errorPrefix: '发布接口请求失败',
  })
  onTrace?.('done', `get_result response · ${JSON.stringify(data)}`)
  return data
}

/**
 * 确认上传文件在服务端的签名和 MD5。
 * @param params 文件名、MD5、工程和发布版本等确认参数。
 * @param onTrace 请求日志回调。
 * @returns get_putsign 接口原始返回。
 */
export async function confirmPublishFilePutSign(
  params: {
    guid: string
    sessionId: string
    version: string
    publishVersion?: string
    fileName: string
    contentMd5: string
    shared: '0' | '1'
  },
  onTrace?: RequestTraceCallback,
): Promise<JsonRecord> {
  const query: Record<string, string | number> = {
    op: '3',
    shared: params.shared,
    version: params.version,
    sessionid: params.sessionId,
    guid: params.guid,
    filename: BrowserFileUtils.stringtoHex(params.fileName),
    content_md5: params.contentMd5,
  }
  if (params.shared === '0' && params.publishVersion) {
    query.ver = params.publishVersion
  }
  const data = await requestJson(webUrl.baseCg, cgEndpoints.getPutSign, {
    params: query,
    signed: true,
    traceLabel: `GET ${cgEndpoints.getPutSign} · ${params.fileName}`,
    onTrace,
    errorPrefix: '发布接口请求失败',
  })
  onTrace?.('done', `get_putsign response · ${params.fileName} · ${JSON.stringify(data)}`)
  return data
}

/**
 * 同步远端工程名。
 * @param sessionId 当前登录 session_id。
 * @param guid 工程 GUID。
 * @param projectName 新作品名。
 * @param onTrace 请求日志回调。
 * @returns 修改作品名接口原始返回。
 */
export async function updateProjectName(
  sessionId: string,
  guid: string,
  projectName: string,
  onTrace?: RequestTraceCallback,
): Promise<JsonRecord> {
  const data = await requestJson(`${webUrl.baseCg}api`, cgApiEndpoints.modifyProject, {
    params: {
      sessionid: sessionId,
      guid,
      op: 0,
      value: projectName,
    },
    traceLabel: `GET ${cgApiEndpoints.modifyProject}`,
    onTrace,
    errorPrefix: '发布接口请求失败',
  })
  return assertCgSuccess(data, '同步作品名称失败')
}

/**
 * 写入发布版本更新说明。
 * @param sessionId 当前登录 session_id。
 * @param payload gindex、更新日志和作者的话。
 * @param onTrace 请求日志回调。
 * @returns 更新说明接口原始返回。
 */
export async function addUpdateLog(
  sessionId: string,
  payload: {
    gindex: string | number
    logIntro: string
    authorYesSay?: string
  },
  onTrace?: RequestTraceCallback,
): Promise<JsonRecord> {
  const body = [
    `gindex=${BrowserFileUtils.stringtoHex(String(payload.gindex))}`,
    `log_intro=${BrowserFileUtils.stringtoHex(payload.logIntro)}`,
    `author_yes_say=${BrowserFileUtils.stringtoHex(payload.authorYesSay ?? '')}`,
    'channel_id=0',
  ].join('&')
  const data = await requestJson(webUrl.baseCg, `${cgEndpoints.addUpdateLog}?sessionid=${encodeURIComponent(sessionId)}&channel_id=0`, {
    method: 'PUT',
    body,
    headers: { 'Content-Type': 'application/octet-stream' },
    traceLabel: `PUT ${cgEndpoints.addUpdateLog}`,
    onTrace,
    errorPrefix: '发布接口请求失败',
  })
  return assertCgSuccess(data, '写入版本说明失败')
}

/**
 * 同步投稿详情、活动和付费属性。
 * @param params 投稿详情接口参数。
 * @param onTrace 请求日志回调。
 * @returns 投稿详情接口原始返回。
 */
export async function addGameAttrs(
  params: Record<string, string | number>,
  onTrace?: RequestTraceCallback,
): Promise<JsonRecord> {
  const data = await requestJson(webUrl.baseCg, cgEndpoints.addGameAttr, {
    params,
    traceLabel: `GET ${cgEndpoints.addGameAttr}`,
    onTrace,
    errorPrefix: '发布接口请求失败',
  })
  return assertCgSuccess(data, '同步投稿详情失败')
}

/**
 * 获取发布工具网页侧配置数据。
 * @param params 网页侧工具配置接口参数。
 * @returns 发布工具网页侧配置数据。
 */
export async function getPublishToolData(
  params: Record<string, string | number | boolean | null | undefined>,
): Promise<JsonRecord> {
  return requestJson(webUrl.baseWebsite, websiteEndpoints.publishToolData, {
    params,
    signed: true,
    errorPrefix: '发布接口请求失败',
  })
}

/**
 * 获取当前发布工具版本号。
 * @returns 旧发布接口签名使用的工具版本号。
 */
export function getPublishToolVersion(): string {
  return RequestUtils.getToolVersion()
}
